import { useState, useEffect, useCallback, useRef } from 'react';
import type { LoanType } from '@/types';
import type { MergedQuestionnaireForm, QuestionnaireResponse } from '@/types/questionnaire';
import { getQuestionnaireForm, getQuestionnaireFormForLead } from '@/services/questionnaireService';

// Cache for loaded forms to avoid refetching
const formCache = new Map<string, MergedQuestionnaireForm>();

interface UseQuestionnaireFormOptions {
  /** Whether to skip loading on mount */
  skip?: boolean;
  /** Cache time in milliseconds (default: 5 minutes) */
  cacheTime?: number;
  /** Lead ID for version-aware form fetching */
  leadId?: string;
}

interface UseQuestionnaireFormResult {
  /** The loaded form (null if not loaded yet) */
  form: MergedQuestionnaireForm | null;
  /** Whether the form is currently loading */
  isLoading: boolean;
  /** Error message if loading failed */
  error: string | null;
  /** Whether the form was loaded from cache */
  isFromCache: boolean;
  /** Whether the form was loaded from static fallback */
  isFromFallback: boolean;
  /** Whether the form version is locked (lead has existing response) */
  isVersionLocked: boolean;
  /** Existing questionnaire response for this lead (if any) */
  existingResponse: QuestionnaireResponse | null;
  /** Manually refresh the form */
  refresh: () => Promise<void>;
}

/**
 * Hook for fetching questionnaire forms with caching and fallback
 * Supports version-aware form fetching when leadId is provided
 * 
 * @param loanType - The loan type to fetch the form for
 * @param lenderId - Optional lender ID for lender-specific forms
 * @param options - Configuration options
 * @returns Form data, loading state, and error state
 * 
 * @example
 * ```tsx
 * // Basic usage
 * const { form, isLoading, error } = useQuestionnaireForm('home_loan', 'hdfc');
 * 
 * // Version-aware usage for a specific lead
 * const { form, isLoading, isVersionLocked } = useQuestionnaireForm('home_loan', 'hdfc', { leadId: 'l123' });
 * // If the lead has an existing response with version lock, the form will use the stored snapshot
 * ```
 */
export const useQuestionnaireForm = (
  loanType: LoanType,
  lenderId?: string,
  options: UseQuestionnaireFormOptions = {}
): UseQuestionnaireFormResult => {
  const { skip = false, cacheTime = 5 * 60 * 1000, leadId } = options;
  
  const [form, setForm] = useState<MergedQuestionnaireForm | null>(null);
  const [isLoading, setIsLoading] = useState(!skip);
  const [error, setError] = useState<string | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [isFromFallback, setIsFromFallback] = useState(false);
  const [isVersionLocked, setIsVersionLocked] = useState(false);
  const [existingResponse, setExistingResponse] = useState<QuestionnaireResponse | null>(null);
  
  // Track if component is mounted to avoid state updates after unmount
  const isMountedRef = useRef(true);
  
  // Create a cache key from loanType, lenderId, and leadId
  const getCacheKey = useCallback(() => {
    // If leadId is provided, include it in cache key for version-aware caching
    if (leadId) {
      return `${loanType}_${lenderId || 'base'}_lead_${leadId}`;
    }
    return lenderId ? `${loanType}_${lenderId}` : loanType;
  }, [loanType, lenderId, leadId]);
  
  // Fetch the form
  const fetchForm = useCallback(async (skipCache = false) => {
    if (!isMountedRef.current) return;
    
    const cacheKey = getCacheKey();
    
    // Check cache first unless skipCache is true (but not for lead-specific requests)
    if (!skipCache && !leadId && formCache.has(cacheKey)) {
      const cachedForm = formCache.get(cacheKey)!;
      setForm(cachedForm);
      setIsFromCache(true);
      setIsFromFallback(cachedForm.source === 'static');
      setIsLoading(false);
      setError(null);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setIsFromCache(false);
    
    try {
      let loadedForm: MergedQuestionnaireForm;
      
      // If leadId is provided, use version-aware fetching
      if (leadId) {
        const result = await getQuestionnaireFormForLead(leadId, loanType, lenderId);
        loadedForm = result.form;
        setIsVersionLocked(result.isVersionLocked);
        setExistingResponse(result.existingResponse);
        
        if (result.isVersionLocked) {
          console.log(`[useQuestionnaireForm] Using version-locked form for lead ${leadId}`);
        }
      } else {
        loadedForm = await getQuestionnaireForm(loanType, lenderId);
        setIsVersionLocked(false);
        setExistingResponse(null);
      }
      
      if (!isMountedRef.current) return;
      
      // Cache the form (but not lead-specific version-locked forms)
      if (!leadId) {
        formCache.set(cacheKey, loadedForm);
        
        // Set cache expiry
        setTimeout(() => {
          formCache.delete(cacheKey);
        }, cacheTime);
      }
      
      setForm(loadedForm);
      setIsFromFallback(loadedForm.source === 'static');
      setIsLoading(false);
      
      console.log(`[useQuestionnaireForm] Loaded form for ${cacheKey} from ${loadedForm.source}`);
    } catch (err) {
      if (!isMountedRef.current) return;
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to load form';
      setError(errorMessage);
      setIsLoading(false);
      
      console.error(`[useQuestionnaireForm] Error loading form for ${cacheKey}:`, err);
    }
  }, [loanType, lenderId, leadId, getCacheKey, cacheTime]);
  
  // Refresh function that bypasses cache
  const refresh = useCallback(async () => {
    await fetchForm(true);
  }, [fetchForm]);
  
  // Load form on mount or when dependencies change
  useEffect(() => {
    isMountedRef.current = true;
    
    if (!skip) {
      fetchForm();
    }
    
    return () => {
      isMountedRef.current = false;
    };
  }, [loanType, lenderId, leadId, skip, fetchForm]);
  
  return {
    form,
    isLoading,
    error,
    isFromCache,
    isFromFallback,
    isVersionLocked,
    existingResponse,
    refresh,
  };
};

/**
 * Prefetch a form into the cache
 * Useful for preloading forms before they're needed
 */
export const prefetchQuestionnaireForm = async (
  loanType: LoanType,
  lenderId?: string
): Promise<void> => {
  const cacheKey = lenderId ? `${loanType}_${lenderId}` : loanType;
  
  if (formCache.has(cacheKey)) {
    return; // Already cached
  }
  
  try {
    const form = await getQuestionnaireForm(loanType, lenderId);
    formCache.set(cacheKey, form);
    console.log(`[prefetchQuestionnaireForm] Prefetched form for ${cacheKey}`);
  } catch (err) {
    console.error(`[prefetchQuestionnaireForm] Failed to prefetch form for ${cacheKey}:`, err);
  }
};

/**
 * Clear the form cache
 * Useful for forcing fresh data after changes
 */
export const clearQuestionnaireFormCache = (): void => {
  formCache.clear();
  console.log('[clearQuestionnaireFormCache] Cache cleared');
};

/**
 * Get cache status for debugging
 */
export const getQuestionnaireFormCacheStatus = (): { size: number; keys: string[] } => {
  return {
    size: formCache.size,
    keys: Array.from(formCache.keys()),
  };
};

export default useQuestionnaireForm;
